import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import { useSelector } from "react-redux";
import { useGetMemberOrganizationsQuery } from "api/account/accountApiSlice";
import { MemberOrganization } from "api/account/types";
import { RootState } from "../../../redux/store";
import { makeOrganizationBookingsPath } from "../common/organizationAdmin";

/**
 * "Your organizations' bookings" — the entry to each organization's plan
 * (#3037, M6). Every approved member sees it; owners additionally cancel.
 *
 * Same stance as `OrganizationAdminSection`: the profile is only a cheap
 * pre-check for whether to ask at all, and what renders comes from the
 * endpoint, which excludes archived organizations the profile still lists.
 */
const OrganizationPlanSection: React.FC = () => {
  const { t } = useTranslation();
  const profile = useSelector((state: RootState) => state.account.profile);

  const mightBeMember = Boolean(
    profile?.memberships?.some((membership) => membership.status === "approved")
  );

  const { data, isError } = useGetMemberOrganizationsQuery(undefined, {
    skip: !mightBeMember,
  });
  const organizations: MemberOrganization[] = Array.isArray(data) ? data : [];

  if (!mightBeMember) return null;

  // An error must not read as "you belong to nothing".
  if (isError) {
    return (
      <div className="space-y-3">
        <h2 className="text-sm font-medium text-slate-700">
          {t("account.organization_plan.section_title")}
        </h2>
        <p className="text-sm text-slate-600">{t("account.organization_plan.load_error")}</p>
      </div>
    );
  }

  if (organizations.length === 0) return null;

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-medium text-slate-700">
        {t("account.organization_plan.section_title")}
      </h2>
      <ul className="space-y-2">
        {organizations.map((organization) => (
          <li key={organization.organizationId}>
            <Link
              to={makeOrganizationBookingsPath(organization.organizationId)}
              className="flex items-center justify-between gap-3 rounded-md bg-primary/5 px-3 py-2 text-sm text-foreground"
            >
              <span>{organization.organizationName}</span>
              <span className="flex items-center gap-2 text-slate-600">
                <CalendarDays className="h-4 w-4" />
                {organization.canManage
                  ? t("account.organization_plan.manage_bookings")
                  : t("account.organization_plan.view_bookings")}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default OrganizationPlanSection;
